import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { BackButton } from "components/BackButton";
import { Layout } from "components/Layout";
import { ConfirmModal } from "components/ConfirmModal";
import { useFetchPupilsByCategory } from "hooks/useQuery/useFetchPupilsByCategory";
import { useFetchTargetCategories } from "hooks/useQuery/useFetchTargetCategories";
import { removePupilFromCategory } from "api/pupils/removePupilFromCategory";
import { AddPupilsToCategoryModal } from "./AddPupilsToCategoryModal";

export const CategoryGroupDetails = () => {
    const { categoryId } = useParams();
    const history = useHistory();
    const { t } = useTranslation();
    const { schools } = useSelector((state) => state.school);
    
    const { pupils, refetchPupils } = useFetchPupilsByCategory(categoryId);
    const { targetCategories, refetchTargetCategories } = useFetchTargetCategories();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pupilToRemove, setPupilToRemove] = useState(null);

    const category = targetCategories.find((cat) => cat._id === categoryId);

    const removeMutation = useMutation({
        mutationFn: ({ pupilId }) => removePupilFromCategory(pupilId, categoryId),
        onSuccess: () => {
            refetchPupils();
            refetchTargetCategories(); // Обновляем счетчики на главной странице
        },
    });

    const handleRemovePupil = (e, pupilId) => {
        e.stopPropagation();
        setPupilToRemove(pupilId);
        setIsConfirmOpen(true);
    };

    const confirmRemovePupil = () => {
        if (pupilToRemove) {
            removeMutation.mutate({ pupilId: pupilToRemove });
            setPupilToRemove(null);
        }
    };

    const handlePupilClick = (pupilId) => {
        history.push(`/pupils/${pupilId}?from=riskGroups&categoryId=${categoryId}`);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        refetchPupils();
        refetchTargetCategories(); // Обновляем счетчики на главной странице
    };

    return (
        <Layout>
            <div className="page">
                <header className="page__header">
                    <BackButton />
                    <p className="page__title">{category?.title || "Целевая группа"}</p>
                </header>

                <div className="school-page__wrapper">
                    <div className="list gesturedZone risk-groups__list">
                        {pupils?.map((pupil) => {
                            const school = schools.find(({ _id }) => _id === pupil.class.school);

                            return (
                                <div
                                    key={pupil._id}
                                    className="risk-groups__list-item risk-groups__list-item--with-remove"
                                >
                                    <div
                                        onClick={() => handlePupilClick(pupil._id)}
                                        style={{ flex: 1, cursor: "pointer" }}
                                    >
                                        <p>{pupil.name} {pupil.surname}</p>
                                        <p className="risk-groups__school">
                                            {school && <span>{school.name},</span>}{" "}
                                            <span>{pupil.class.number} {pupil.class.letter}</span>
                                        </p>
                                    </div>

                                    <button
                                        className="risk-groups__remove-btn"
                                        onClick={(e) => handleRemovePupil(e, pupil._id)}
                                        title="Исключить из целевой группы"
                                    >
                                        −
                                    </button>
                                </div>
                            );
                        })}

                        {pupils?.length === 0 && (
                            <p style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
                                В этой целевой группе пока нет учеников
                            </p>
                        )}
                    </div>

                    <button
                        className="btn btn_primary"
                        style={{ marginTop: "2rem", width: "100%" }}
                        onClick={() => setIsModalOpen(true)}
                    >
                        Добавить учеников в ЦГ
                    </button>
                </div>

                {isModalOpen && (
                    <AddPupilsToCategoryModal
                        categoryId={categoryId}
                        onClose={handleModalClose}
                    />
                )}

                <ConfirmModal
                    isOpen={isConfirmOpen}
                    onClose={() => setIsConfirmOpen(false)}
                    onConfirm={confirmRemovePupil}
                    title="Исключить ученика"
                    message="Вы уверены, что хотите исключить ученика из целевой группы?"
                    confirmText="Исключить"
                    cancelText="Отмена"
                />
            </div>
        </Layout>
    );
};
